<script setup>
/**
 * Manages the WhatsApp template library for a Cloud API inbox.
 *
 * Templates are reviewed by Meta after they are submitted, so a new one shows up as PENDING and
 * flips to APPROVED or REJECTED once the `message_template_status_update` webhook lands. Refreshing
 * the inbox is what surfaces that change here.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import InboxesAPI from 'dashboard/api/inboxes';

import Button from 'dashboard/components-next/button/Button.vue';
import WhatsappTemplateForm from './components/WhatsappTemplateForm.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();

const STATUS_CLASSES = {
  APPROVED: 'bg-n-teal-3 text-n-teal-11',
  REJECTED: 'bg-n-ruby-3 text-n-ruby-11',
  PENDING: 'bg-n-amber-3 text-n-amber-11',
};

const isFormOpen = ref(false);
const isSubmitting = ref(false);
const isSyncing = ref(false);
const deletingTemplate = ref('');

const templates = computed(() => props.inbox.message_templates || []);

const statusClass = status =>
  STATUS_CLASSES[String(status).toUpperCase()] ||
  'bg-n-alpha-2 text-n-slate-11';

const templateBody = template =>
  (template.components || []).find(component => component.type === 'BODY')
    ?.text || '';

const refreshInbox = () => store.dispatch('inboxes/get', props.inbox.id);

const syncTemplates = async () => {
  isSyncing.value = true;
  try {
    await InboxesAPI.syncTemplates(props.inbox.id);
    await refreshInbox();
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC.SUCCESS'));
  } catch (error) {
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC.ERROR'));
  } finally {
    isSyncing.value = false;
  }
};

// Closing the form unmounts it, which is what clears what was typed into it.
const createTemplate = async template => {
  isSubmitting.value = true;
  try {
    await InboxesAPI.createWhatsappTemplate(props.inbox.id, template);
    await refreshInbox();
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.SUCCESS'));
    isFormOpen.value = false;
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.ERROR')
    );
  } finally {
    isSubmitting.value = false;
  }
};

const deleteTemplate = async name => {
  deletingTemplate.value = name;
  try {
    await InboxesAPI.deleteWhatsappTemplate(props.inbox.id, name);
    await refreshInbox();
    useAlert(t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE.ERROR')
    );
  } finally {
    deletingTemplate.value = '';
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div class="flex flex-col gap-1">
        <h3 class="text-heading-3 text-n-slate-12">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.TITLE') }}
        </h3>
        <p class="text-label-small text-n-slate-11">
          {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.DESCRIPTION') }}
        </p>
      </div>
      <div class="flex items-center gap-2">
        <Button
          :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.SYNC.BUTTON')"
          size="sm"
          variant="outline"
          color="slate"
          :is-loading="isSyncing"
          :disabled="isSyncing"
          data-testid="sync-templates"
          @click="syncTemplates"
        />
        <Button
          :label="
            isFormOpen
              ? t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.CANCEL')
              : t('INBOX_MGMT.WHATSAPP_TEMPLATES.CREATE.BUTTON')
          "
          size="sm"
          data-testid="create-template"
          @click="isFormOpen = !isFormOpen"
        />
      </div>
    </div>

    <WhatsappTemplateForm
      v-if="isFormOpen"
      :is-submitting="isSubmitting"
      @submit="createTemplate"
    />

    <p v-if="!templates.length" class="text-sm text-n-slate-11">
      {{ t('INBOX_MGMT.WHATSAPP_TEMPLATES.EMPTY') }}
    </p>

    <ul v-else class="flex flex-col divide-y divide-n-weak">
      <li
        v-for="template in templates"
        :key="`${template.name}-${template.language}`"
        class="flex flex-wrap items-start justify-between gap-3 py-3"
      >
        <div class="flex flex-col min-w-0 gap-1">
          <div class="flex flex-wrap items-center gap-2">
            <span class="text-sm font-medium truncate text-n-slate-12">
              {{ template.name }}
            </span>
            <span
              class="px-2 py-0.5 rounded-full text-[10px] font-bold tracking-wide uppercase"
              :class="statusClass(template.status)"
            >
              {{ template.status }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ template.category }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ template.language }}
            </span>
          </div>
          <p class="text-sm break-words text-n-slate-11">
            {{ templateBody(template) }}
          </p>
          <p
            v-if="
              template.rejected_reason && template.rejected_reason !== 'NONE'
            "
            class="text-xs text-n-ruby-11"
          >
            {{ template.rejected_reason }}
          </p>
        </div>
        <Button
          :label="t('INBOX_MGMT.WHATSAPP_TEMPLATES.DELETE.BUTTON')"
          size="sm"
          variant="outline"
          color="ruby"
          :is-loading="deletingTemplate === template.name"
          :disabled="deletingTemplate === template.name"
          :data-testid="`delete-${template.name}`"
          @click="deleteTemplate(template.name)"
        />
      </li>
    </ul>
  </div>
</template>
